import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type ApiClient } from "../../api/client";
import type { Authorization, TermsTexts } from "../../api/types";
import { t } from "../../i18n";
import { Markdown } from "../../lib/markdown";
import { formatDate } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/toasts";
import { CodeDialog } from "../codes/CodeDialog";
import { dateTime } from "../subscription/subscription";

export const LEGAL_DOCUMENTS = ["terms", "dpa", "privacy"] as const;
export type LegalDocument = (typeof LEGAL_DOCUMENTS)[number];

/** "first": never accepted; "reaccept": a new version is in effect; "new": a version announced for later. */
export type GateMode = "first" | "reaccept" | "new";

/** Counts as read to the end from this percentage on (prototype). */
const READ_PERCENT = 98;

interface Props {
  api: ApiClient;
  mode: GateMode;
  onAccepted: () => void;
  /** Without it the screen cannot be closed: nobody can work before the owner accepted. */
  onClose?: () => void;
}

/**
 * Accepting the terms, as in the prototype (openAkkGate) with the approved in-app texts (Deel D a2):
 * the full texts, read to the end before the first box can be ticked, two boxes, the owner's name and
 * the owner's own code. The owner can also be sent the personal link instead.
 */
export function TermsGate({ api, mode, onAccepted, onClose }: Props) {
  const toast = useToast();
  const [data, setData] = useState<TermsTexts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [percent, setPercent] = useState(0);
  const [read, setRead] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [confirmOnline, setConfirmOnline] = useState(false);
  const [name, setName] = useState("");
  const [linkSentAt, setLinkSentAt] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const text = t.terms;

  useEffect(() => {
    const controller = new AbortController();
    api
      .request<TermsTexts>("GET", "/device/terms", { retry: true, signal: controller.signal })
      .then((answer) => {
        setData(answer);
        setName(answer.shop.ownerName);
        setLinkSentAt(answer.shop.linkSentAt);
      })
      .catch((failure: unknown) => {
        if (!controller.signal.aborted) setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      });
    return () => {
      controller.abort();
    };
  }, [api]);

  const measure = useCallback(() => {
    const element = scroller.current;
    if (!element) return;
    const seen = Math.min(100, Math.round(((element.scrollTop + element.clientHeight) / element.scrollHeight) * 100));
    setPercent(seen);
    if (seen >= READ_PERCENT) setRead(true);
  }, []);
  // a text that fits without scrolling is read at once
  useEffect(() => {
    if (data) measure();
  }, [data, measure]);

  const accept = async (owner: Authorization) => {
    setError(null);
    try {
      await api.request("POST", "/device/terms/acceptance", {
        staffToken: owner.token,
        body: { name: name.trim(), confirmations: [true, true], readToEnd: true },
      });
      toast(text.accepted(data?.shop.ownerEmail ?? ""));
      onAccepted();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
    }
  };

  const sendLink = async () => {
    setError(null);
    try {
      await api.request("POST", "/device/terms/link");
      setLinkSentAt(new Date().toISOString());
      toast(text.linkSent(data?.shop.ownerEmail ?? ""));
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
    }
  };

  const from = data ? formatDate(data.effectiveFrom) : "";
  const subtitle =
    mode === "first"
      ? text.subtitle.first
      : data === null
        ? undefined
        : mode === "reaccept"
          ? text.subtitle.reaccept(data.version, from)
          : text.subtitle.new(data.version, from);
  const ready = read && confirmTerms && confirmOnline && name.trim().length > 1;

  return (
    <>
      <Dialog
        title={text.title}
        subtitle={subtitle}
        size="gate"
        locked={onClose === undefined}
        onClose={onClose ?? (() => undefined)}
        footer={
          <>
            <button type="button" className="btn" disabled={data === null} onClick={() => void sendLink()}>
              {text.sendLink(linkSentAt !== null, data?.shop.ownerEmail ?? "")}
            </button>
            <span className="spacer" />
            <button
              type="button"
              className="btn primary"
              disabled={!ready}
              onClick={() => {
                setAsking(true);
              }}
            >
              {text.accept}
            </button>
          </>
        }
      >
        <div className="akkpage">
          {mode !== "first" && data?.summary && (
            <p className="note akkwhat">
              <b>{text.whatChanges}</b> {data.summary}
            </p>
          )}
          <p className="note">
            {read ? (
              text.readDone
            ) : (
              <>
                {text.readHint} (<b>{percent}%</b>).
              </>
            )}
          </p>
          <div className="akkscroll legal-doc" ref={scroller} onScroll={measure} tabIndex={0} aria-label={text.title}>
            {data &&
              LEGAL_DOCUMENTS.map((document, index) => (
                <Fragment key={document}>
                  {index > 0 && <hr />}
                  <section aria-label={text.documents[document]}>
                    <Markdown source={data.texts[document]} />
                  </section>
                </Fragment>
              ))}
          </div>
          <label className="akkchk">
            <input
              type="checkbox"
              checked={confirmTerms}
              disabled={!read}
              onChange={(event) => {
                setConfirmTerms(event.target.checked);
              }}
            />
            <span>{text.confirmTerms(data?.shop.firmName ?? "", data?.version ?? "")}</span>
          </label>
          <label className="akkchk">
            <input
              type="checkbox"
              checked={confirmOnline}
              onChange={(event) => {
                setConfirmOnline(event.target.checked);
              }}
            />
            <span>{text.confirmOnline}</span>
          </label>
          <label className="field">
            {text.name}
            <input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              autoComplete="name"
              maxLength={120}
            />
          </label>
          <p className="note">
            {text.noOwner}
            {linkSentAt !== null && ` ${text.linkSentAt(dateTime(linkSentAt))}`}
          </p>
          {error !== null && (
            <p className="error-text" role="alert">
              {error}
            </p>
          )}
        </div>
      </Dialog>
      {asking && (
        <CodeDialog
          api={api}
          permission="terms.accept"
          title={text.accept}
          onAuthorized={(owner) => {
            setAsking(false);
            void accept(owner);
          }}
          onClose={() => {
            setAsking(false);
          }}
        />
      )}
    </>
  );
}
